import { isExtractionTransferReady } from './recognized-stage'
import { isValidSnils } from '../../../hooks/useForm'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'

const unconfirmedLinks = new Set(['person_link_unconfirmed', 'document_link_unconfirmed'])

const formatExtractedSnils = (value: string): string | null => {
    const text = value.normalize('NFKC').trim()
    if (!/^[0-9\s\-‐‑‒–—−]+$/.test(text)) return null
    const digits = text.replace(/\D/g, '')
    if (!/^\d{11}$/.test(digits) || /^0+$/.test(digits)) return null
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)} ${digits.slice(9)}`
}

export const normalizeExtractedSnils = (value: string): string | null => {
    const formatted = formatExtractedSnils(value)
    return formatted && isValidSnils(formatted) ? formatted : null
}

type SnilsReviewIssue = { number: string | null; message: string }

export const inspectRecognizedSnils = (stage: ExtractionStage | null): { numbers: string[]; needsReview: boolean; issues: SnilsReviewIssue[] } => {
    const numbers: string[] = []
    const issues: SnilsReviewIssue[] = []
    if (!stage || !isExtractionTransferReady(stage)) return { numbers, needsReview: false, issues }
    for (const part of stage.parts) {
        if (part.status !== 'succeeded' || !part.result) continue
        for (const document of part.result.documents) {
            if (document.type !== 'snils') continue
            const field = document.fields.find(item => item.name === 'number')
            if (!field || (!field.rawValue?.trim() && !field.normalizedValue?.trim())) continue
            const raw = field.rawValue?.trim() ? normalizeExtractedSnils(field.rawValue) : null
            const normalized = field.normalizedValue?.trim() ? normalizeExtractedSnils(field.normalizedValue) : null
            const number = normalized || raw
            const conflicting = part.result.conflicts.some(conflict => !unconfirmedLinks.has(conflict.code)
                && conflict.fields.some(ref => ref.documentId === document.documentId && ref.field === 'number'))
                || part.result.unresolved.some(item => !unconfirmedLinks.has(item.code) && item.documentId === document.documentId && item.field === 'number')
            let message = ''
            const printed = field.rawValue?.trim() ? formatExtractedSnils(field.rawValue) : null
            const proposed = field.normalizedValue?.trim() ? formatExtractedSnils(field.normalizedValue) : null
            if ((printed && proposed && printed !== proposed) || (raw && normalized && raw !== normalized)) {
                message = 'Исходный и нормализованный номера СНИЛС отличаются. Проверьте номер по оригиналу.'
            } else if ((printed && !raw) || (proposed && !normalized)) {
                message = 'Контрольное число СНИЛС не совпадает с первыми девятью цифрами. Проверьте все 11 цифр по оригиналу и исправьте номер в «Основных данных».'
            } else if (!number
                || (field.rawValue?.trim() && !raw) || (field.normalizedValue?.trim() && !normalized)
            ) {
                message = 'СНИЛС должен содержать 11 цифр. Проверьте номер и разделители по оригиналу.'
            } else if (conflicting || ['failed', 'invalid', 'conflict'].includes(field.validation.crossDocument)) {
                message = 'В результатах есть противоречие по номеру СНИЛС. Проверьте номер по оригиналу.'
            } else if (field.candidates.length || field.status !== 'extracted') {
                message = 'Номер СНИЛС распознан неоднозначно. Проверьте все цифры по оригиналу.'
            } else if (['failed', 'invalid', 'conflict'].includes(field.validation.format)) {
                message = 'Номер СНИЛС не прошёл проверку формата в распознавании. Проверьте номер по оригиналу.'
            }
            if (message) {
                const issue = { number: printed || proposed, message }
                if (!issues.some(item => item.number === issue.number && item.message === issue.message)) issues.push(issue)
                continue
            }
            if (number && !numbers.includes(number)) numbers.push(number)
        }
    }
    return { numbers, needsReview: issues.length > 0, issues }
}

export const recognizedSnilsNumbers = (stage: ExtractionStage | null): { numbers: string[]; needsReview: boolean } => {
    const { numbers, needsReview } = inspectRecognizedSnils(stage)
    return { numbers, needsReview }
}
